import { TimeMaterialBoardAPI } from '@business/api/tm_board';
import { Blocs } from '@business/blocs';
import { TMBoardId, TimeMaterialBoardResponse } from '@business/entities/tm/TimeMaterialBoardResponse';
import { StageId } from '@business/entities/project/ProjectStageResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { TimeMaterialsFilterData } from '@modules/timeMaterials/TimeMaterialsPage/TimeMaterialsPage';
import { memoize } from '@core/utils/rxjs/operators';
import { AppConfig } from '@core/utils/config';

export class TimeMaterialBoardBloc extends BaseBloc {
  private readonly boardRepo = new Repository<TimeMaterialBoardResponse>({
    getItemId: board => board.id,
    pagenated: true,
  });

  readonly boards$ = this.boardRepo.items$;

  readonly selectTimeMaterialBoard = this.boardRepo.selectItem;

  onReset() {
    this.boardRepo.reset();
  }

  fetchTimeMaterialBoards = (permission = true, isCompact = false) => {
    return TimeMaterialBoardAPI.fetchTimeMaterialBoards(permission, isCompact).pipe(
      this.boardRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_board_error') }),
    );
  };

  fetchTimeMaterialBoard = (id: TMBoardId, data?: Partial<TimeMaterialsFilterData>, isVeryCompact?: boolean) => {
    return TimeMaterialBoardAPI.fetchTimeMaterialBoard(id, data, isVeryCompact).pipe(
      this.boardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_board_error') }),
    );
  };

  createTimeMaterialBoard = (data: Partial<TimeMaterialBoardResponse>) => {
    return TimeMaterialBoardAPI.createTimeMaterialBoard(data).pipe(
      this.boardRepo.ops.addOne(item => item),
      switchMap(() => resolve(Blocs.settings).fetchSettings()),
      handleMessage({
        type: requestMessage('create_board'),
      }),
    );
  };

  updateTimeMaterialBoard = (id: string, data: Partial<TimeMaterialBoardResponse>) => {
    return TimeMaterialBoardAPI.updateTimeMaterialBoard(id, data).pipe(
      this.boardRepo.ops.upsertOne(item => ({ item })),
      switchMap(() => resolve(Blocs.settings).fetchSettings()),
      handleMessage({
        type: requestMessage('update_board'),
      }),
    );
  };

  deleteTimeMaterialBoard = (id: string) => {
    return showDeleteConfirmation('Delete TimeMaterialBoard', 'Do you really want to remove this board?').pipe(
      switchMap(() => TimeMaterialBoardAPI.deleteTimeMaterialBoard(id)),
      this.boardRepo.ops.removeOne(() => id),
      switchMap(() => resolve(Blocs.settings).fetchSettings()),
      handleMessage({
        type: requestMessage('delete_board'),
      }),
    );
  };
}
